import { alpha, createTheme } from "@mui/material/styles";

const red = "#E31B23";
const black = "#0A0A0B";
const line = "#DDE1E6";

export const spend2ConnectTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: red,
      dark: "#B8131A",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: black,
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#F4F5F7",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#17181B",
      secondary: "#626874",
    },
    divider: line,
    success: {
      main: "#16834A",
    },
    warning: {
      main: "#A56200",
    },
    error: {
      main: "#C9151C",
    },
  },

  shape: {
    borderRadius: 0,
  },

  // A readable desktop scale while keeping the portal compact.
  typography: {
    fontFamily: '"Manrope", "Segoe UI", Arial, sans-serif',
    fontSize: 14,
    h1: {
      fontSize: "1.375rem",
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: "-0.02em",
    },
    h3: {
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h4: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "0.9375rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: "0.875rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: "0.875rem",
      lineHeight: 1.55,
    },
    body2: {
      fontSize: "0.8125rem",
      lineHeight: 1.5,
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.45,
    },
    button: {
      fontSize: "0.8125rem",
      fontWeight: 600,
      textTransform: "none",
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          minWidth: 320,
          backgroundColor: "#F4F5F7",
        },
        body: {
          minWidth: 320,
          minHeight: "100vh",
          backgroundColor: "#F4F5F7",
          color: "#17181B",
        },
        "*": {
          borderRadius: "0 !important",
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        outlined: {
          borderColor: line,
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: "small",
      },
      styleOverrides: {
        root: {
          minHeight: 34,
          padding: "0 12px",
          borderRadius: 0,
          boxShadow: "none",
          whiteSpace: "nowrap",
        },
        containedPrimary: {
          borderLeft: `3px solid ${black}`,
          "&:hover": {
            backgroundColor: black,
            borderLeftColor: red,
            boxShadow: "none",
          },
        },
        outlined: {
          borderColor: "#C7CCD4",
          color: "#24272C",
          "&:hover": {
            borderColor: black,
            backgroundColor: "#FAFAFB",
          },
        },
      },
    },

    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          width: 32,
          height: 32,
          borderRadius: 0,
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        size: "small",
        variant: "outlined",
      },
    },

    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 36,
          borderRadius: 0,
          backgroundColor: "#FFFFFF",
          fontSize: "0.8125rem",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#C9CED6",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#7E8590",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: red,
            borderWidth: 1,
            boxShadow: `0 0 0 2px ${alpha(red, 0.08)}`,
          },
        },
        input: {
          padding: "8px 10px",
        },
        inputSizeSmall: {
          padding: "8px 10px",
        },
        multiline: {
          padding: 0,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          "&.Mui-focused": {
            color: red,
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          fontSize: "0.75rem",
        },
      },
    },

    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: "#7B828E",
          "& .MuiSvgIcon-root": {
            fontSize: "1.1rem",
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: "auto",
          paddingTop: 8,
          paddingBottom: 8,
          fontSize: "0.8125rem",
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 34,
          fontSize: "0.8125rem",
          "&.Mui-selected": {
            backgroundColor: alpha(red, 0.08),
          },
          "&.Mui-selected:hover": {
            backgroundColor: alpha(red, 0.12),
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 38,
        },
        indicator: {
          height: 2,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 38,
          minWidth: 88,
          padding: "0 14px",
          fontSize: "0.8125rem",
          fontWeight: 600,
          textTransform: "none",
        },
      },
    },

    MuiTable: {
      defaultProps: {
        size: "small",
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          height: 42,
          padding: "7px 10px",
          borderColor: "#E6E8EC",
          color: "#41464F",
          fontSize: "0.8125rem",
        },
        head: {
          height: 36,
          paddingTop: 6,
          paddingBottom: 6,
          color: "#6A717D",
          backgroundColor: "#F8F9FA",
          fontSize: "0.75rem",
          fontWeight: 700,
          letterSpacing: "0.035em",
          textTransform: "uppercase",
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "#FCFCFD",
          },
        },
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          minHeight: 44,
          paddingRight: 10,
          fontSize: "0.75rem",
        },
        selectLabel: {
          fontSize: "0.75rem",
        },
        displayedRows: {
          fontSize: "0.75rem",
        },
        select: {
          fontSize: "0.75rem",
        },
      },
    },

    MuiChip: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          height: 24,
          borderRadius: 0,
          fontSize: "0.75rem",
          fontWeight: 600,
        },
        label: {
          paddingLeft: 7,
          paddingRight: 7,
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontSize: "0.75rem",
          fontWeight: 700,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "14px 16px",
          fontSize: "1rem",
          fontWeight: 600,
          borderBottom: `1px solid ${line}`,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 16,
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "10px 16px",
          borderTop: `1px solid ${line}`,
        },
      },
    },

    MuiBreadcrumbs: {
      styleOverrides: {
        ol: {
          flexWrap: "nowrap",
        },
        li: {
          fontSize: "0.75rem",
          whiteSpace: "nowrap",
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          borderColor: line,
          backgroundColor: "#FFFFFF",
          fontSize: "0.8125rem",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F8F9FA",
            borderBottomColor: line,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "#69717D",
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.035em",
            textTransform: "uppercase",
          },
          "& .MuiDataGrid-cell": {
            borderBottomColor: "#EAECF0",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FCFCFD",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-footerContainer": {
            minHeight: 42,
            borderTopColor: line,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 0,
          backgroundColor: "#17181B",
          fontSize: "0.75rem",
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
        thumb: {
          borderRadius: 0,
        },
      },
    },
  },
});
